import { ExclamationCircleOutlined } from "@ant-design/icons";
import { HEATMAP_TAB_TITLE } from "@const/dashboard.constants";
import { BOX_TYPE } from "@const/pageResponse.constants";
import { CustomGroupFavoriteResponseDTO } from "@dto/customGroup.dto";
import { Box, BoxType, Export, SubTab, SummaryBox, Tab, TabResponseDTO } from "@dto/pageResponse.dto";
import { SummaryDashBtn } from "@parts/summary/SummaryDashBtn";
import { AxiosError } from "axios";
import { Dispatch, FC, lazy, RefObject, SetStateAction, Suspense, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAppGlobStore } from "src/appGlobalStore";
import { XmBreadcrumb } from "src/component/breadcrumb/XmBreadcrumb";
import { getCgFavoriteURL } from "src/component/breadcrumb/XmCgHeatmapFavorite";
import { XmHelp } from "src/component/help/XmHelp";
import { XmSpinner } from "src/component/spinner/XmSpinner";
import XmTabsSticky from "src/component/stickyTab/XmTabsSticky";
import { RoutePath } from "src/data/Routes";
import { getTabKey, hashRecordToStr, parseHash, TabHash, TabPaneContext } from "src/routing/tabHash";
import { Log } from "src/service/log.service";
import { getApi } from "src/util/apiCalls";
import { GLOB } from "src/util/Glob";
import XmExportContentIcon from "../../component/apiDownloader/XmExportContentIcon";

interface Props {
  tab: Tab;
  url?: string;
  renderBreadcrumb?: boolean;
  topTabName?: string;
}

const BoxPage = ({ tab: { title, content, lazyContent, export: exportProp, help, subTabs }, url, renderBreadcrumb = true, topTabName }: Props) => {
  const [loadedTab, setLoadedTab] = useState<Tab<Box>>();
  const [exportPropFiltered, setExportPropFiltered] = useState<Export>(exportProp);

  useEffect(() => {
    const ac = new AbortController();
    if (lazyContent) {
      //TODO: React19 use(resource)
      getApi<TabResponseDTO>(lazyContent, ac.signal).then(response => {
        setLoadedTab(response.data.data);
      }, (reason: AxiosError) => Log.error('Failed to load lazy tab!', reason));
    }
    if (GLOB.selectedClass === RoutePath.CUSTOM_GROUPS && title === HEATMAP_TAB_TITLE) {
      getApi<CustomGroupFavoriteResponseDTO>(getCgFavoriteURL(), ac.signal).then(resp => {
        useAppGlobStore.setState({ isCgHeatDash: resp.data.data.favorite });
      }, (reason: AxiosError) => Log.error('Failed to get CG heatmap favorite!', reason));
    }
    return () => {
      ac.abort();
    };
  }, []);


  if (lazyContent) {
    if (loadedTab)
      return <BoxPage tab={loadedTab} url={url} />;
    else
      return <XmSpinner />;
  }
  if (!content)
    return <div>unknown content</div>;
  if (subTabs) {
    return <SubTabs url={url} subTabs={subTabs} topTabName={title} />;
  }

  const summaryTemplate = findSummaryTemplate(content);

  return (
    <>
      {exportProp && shouldRenderExportIcon(content) ? <XmExportContentIcon exportProp={exportPropFiltered} /> : null}
      {summaryTemplate ? <SummaryDashBtn itemId={summaryTemplate.item_id} /> : null}
      {help && <XmHelp href={help.url} title={help.text} className="xm-tab-help" />}
      {title === 'CPU Ready' && <XmHelp href='https://xormon.com/VMware-CPU-ready.php' className="xm-tab-help" />}
      {renderBreadcrumb && shouldRenderBreadcrumb(content) ?
        <XmBreadcrumb key='xmBC' isSummary={title === "Summary" && !(GLOB.selectedClass === RoutePath.DASHBOARD || GLOB.selectedClass === RoutePath.SETTINGS)} /> : null}
      {resolveBoxComponents(content, title, url, topTabName, exportProp && shouldRenderExportIcon(content) ? setExportPropFiltered : undefined)}
    </>
  );
};

const SubTabs: FC<{ subTabs: SubTab[], url: string, topTabName: string; }> = ({ subTabs, url, topTabName, ...props }) => {
  const [hash, setHash] = useState<TabHash>({});
  const navigate = useNavigate();
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(ref);
  // can't get parent tab from location in react-router v7
  const parentTabKey = getTabKey(topTabName);
  // only the visible pane may write the URL
  const isActiveParent = useContext(TabPaneContext).active;
  // don't clear subTab on initial render
  const wasVisible = useRef(false);

  useEffect(() => {
    let lochash = hash;
    if (location.hash) {
      lochash = parseHash(location);
      if (!subTabs.some(st => getTabKey(st) === lochash.subTab))
        lochash.subTab = getTabKey(subTabs[0]);
      setHash(lochash);
    }
    if (!lochash.subTab && isActiveParent) {
      setActiveTab(hash.subTab || getTabKey(subTabs[0]), lochash);
    }
  }, []);

  useEffect(() => {
    if (!hash.subTab || !isActiveParent) return;
    const lochash = parseHash(location);
    if (isVisible) {
      wasVisible.current = true;
      if (!lochash.subTab)
        setActiveTab(hash.subTab || getTabKey(subTabs[0]), lochash);
    } else if (lochash.subTab && wasVisible.current) {
      delete lochash.subTab;
      const hashStr = hashRecordToStr(lochash);
      navigate(decodeURIComponent(location.pathname + location.search) + hashStr, { replace: true });
    }

  }, [isVisible, hash, isActiveParent]);

  function setActiveTab(tabKey: string, lochash = hash) {
    if (parentTabKey)
      lochash.tab = parentTabKey;
    if (tabKey)
      lochash.subTab = tabKey;
    else
      delete lochash.subTab;

    const hashStr = hashRecordToStr(lochash);
    navigate(decodeURIComponent(location.pathname + location.search) + hashStr, { replace: true });
    setHash({ ...lochash });
  }

  return <div className="xm-subtabs" ref={ref}>
    <XmTabsSticky activeKey={hash.subTab} onChange={setActiveTab}
      items={subTabs.map(st => ({
        key: getTabKey(st),
        label: <TabLabel label={st.title} hash={{ ...hash, ...(parentTabKey ? { tab: parentTabKey } : {}), subTab: getTabKey(st) }} />,
        children: <Suspense fallback={<XmSpinner />}><BoxPage tab={st} url={url} topTabName={topTabName} /></Suspense>
      }))} />
  </div>;
};

export function TabLabel({ label, hash }: { label: string, hash: TabHash; }) {
  const location = useLocation();

  const handleOpenInNewTab = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      const hashStr = hashRecordToStr(hash);
      const url = decodeURIComponent(location.pathname + location.search) + hashStr;
      e.stopPropagation();
      window.open(url, '_blank');
      return;
    }
  };

  return <span onClick={handleOpenInNewTab} onAuxClick={handleOpenInNewTab}>{label}</span>;
}

function useOnScreen(ref: RefObject<HTMLElement | null>) {

  const [isIntersecting, setIntersecting] = useState(false);

  const observer = useMemo(() => new IntersectionObserver(
    ([entry]) => setIntersecting(entry.isIntersecting)
  ), [ref]);


  useEffect(() => {
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return isIntersecting;
}

export function resolveBoxComponents(boxes: Box[], tabName: string, url: string, topTabName?: string, setExportProp?: Dispatch<SetStateAction<Export | undefined>>) {
  return boxes.map((b, index) => <Suspense key={`${b.type}${index}`} fallback={<XmSpinner />}>
    <BoxComponent type={b.type} box={b} tabName={topTabName || tabName} subTabName={topTabName ? tabName : null} url={url} setExportProp={setExportProp} />
  </Suspense>);
}

const shouldRenderBreadcrumb = (content: Box[]) => content.every(({ type }) => !(NO_BREADCRUMB as readonly string[]).includes(type));
//const isCenter = (content: Box[]) => content.some(({ type }) => (CENTER_BOX as ReadonlyArray<string>).includes(type));
const shouldRenderExportIcon = (content: Box[]) => content.some(({ type }) => !(NO_EXPORT_ICON as readonly string[]).includes(type));
const findSummaryTemplate = (content: Box[]) => content.find((box): box is SummaryBox => SummaryBox.isSummaryBox(box) && box.template);

const NO_BREADCRUMB = [
  BOX_TYPE.TopologyRequestBox,
  BOX_TYPE.PortHealthStatusBox,
  BOX_TYPE.HistoricalReportBox,
  BOX_TYPE.SummaryBox,
] as const;

// const CENTER_BOX = [
//   BOX_TYPE.FourGraphBox,
// ] as const;

const NO_EXPORT_ICON = [
  BOX_TYPE.OverviewBox,
] as const;

interface BoxComponentProps {
  type: BoxType;
  tabName: string;
  box: Box;
  url?: string;
  subTabName?: string;
  setExportProp?: Dispatch<SetStateAction<Export | undefined>>;
}

const BoxComponent = ({ type, ...props }: BoxComponentProps) => {
  if (type === 'RegroupButtonBox') return null;

  if (!boxComponent[type])
    return <MissingBox type={type} />;

  const BoxOfType = boxComponent[type];

  if (type !== BOX_TYPE.TopologyRequestBox && type !== BOX_TYPE.OverviewBox)
    return (
      <div className="text-center">
        <BoxOfType {...props} />
      </div>
    );

  return <BoxOfType {...props} />;
};
const boxComponent = {
  [BOX_TYPE.PerformanceBox]: lazy(() => import('@parts/performance/PerformanceBoxPart')),
  [BOX_TYPE.CapacityTableBox]: lazy(() => import('@parts/table/TableCapacityPart')),
  [BOX_TYPE.ConfigurationChangesBox]: lazy(() => import('@parts/cfgTracker/CfgTrackerPart')),
  [BOX_TYPE.CustomTopBox]: lazy(() => import('@parts/customTop/CustomTopPart')),
  [BOX_TYPE.DashboardBox]: lazy(() => import('@parts/dashboard/DashboardPart')),
  [BOX_TYPE.EstimatorBox]: lazy(() => import('@parts/estimator/EstimatorPart')),
  [BOX_TYPE.EstimatorGraphBox]: lazy(() => import('@parts/graph/EstimatorGraphPart')),
  [BOX_TYPE.FourGraphBox]: lazy(() => import('@parts/graph/FourGraphPart')),
  [BOX_TYPE.HealthCheckBox]: lazy(() => import('@parts/HealthCheckPart')),
  [BOX_TYPE.HeatmapBox]: lazy(() => import('@parts/heatmap/HeatmapPart')),
  [BOX_TYPE.HtmlTextBox]: lazy(() => import('@parts/HtmlPart')),
  [BOX_TYPE.LegendBox]: lazy(() => import('@parts/legend/LegendPart')),
  [BOX_TYPE.LogRequestBox]: lazy(() => import('@parts/logRequest/LogRequestPart')),
  [BOX_TYPE.EventRequestBox]: lazy(() => import('@parts/eventRequest/EventRequestPart')),
  [BOX_TYPE.MirrorBox]: lazy(() => import('@parts/mirror/MirrorPart')),
  [BOX_TYPE.MultiGraphBox]: lazy(() => import('@parts/graph/MultiGraphPart')),
  [BOX_TYPE.OverviewBox]: lazy(() => import('@parts/overview/OverviewPart')),
  [BOX_TYPE.PortHealthStatusBox]: lazy(() => import('@parts/sanHealthStatus/SanHealthStatus')),
  [BOX_TYPE.PredictionGraphBox]: lazy(() => import('@parts/graph/PredictionGraphPart')),
  [BOX_TYPE.RegroupedGraphBox]: lazy(() => import('@parts/graph/RegroupedGraphPart')),
  [BOX_TYPE.SingleGraphBox]: lazy(() => import('@parts/graph/SingleGraphPart')),
  [BOX_TYPE.SingleTimeRangeGraphBox]: lazy(() => import('@parts/graph/SingleTimeRangeGraphPart')),
  [BOX_TYPE.StorageHeatmapBox]: lazy(() => import('@parts/heatmap/StorageHeatmapPart')),
  [BOX_TYPE.SyncGraphBox]: lazy(() => import('@parts/graph/SyncGraphPart')),
  [BOX_TYPE.SummaryBox]: lazy(() => import('@parts/summary/SummaryPart')),
  [BOX_TYPE.TableBox]: lazy(() => import('@parts/table/TablePart')),
  [BOX_TYPE.TablePivotBox]: lazy(() => import("@parts/table/TablePivotPart")),
  [BOX_TYPE.TextBox]: lazy(() => import('@parts/TextPart')),
  [BOX_TYPE.TopBox]: lazy(() => import('@parts/table/TopTableGroupPart')),
  [BOX_TYPE.TopologyRequestBox]: lazy(() => import('@parts/topology/TopologyPart')),
  [BOX_TYPE.TableGroupBox]: lazy(() => import('@parts/table/TableGroupPart')),
  [BOX_TYPE.TableHeaderGroupBox]: lazy(() => import('@parts/table/TableHeaderGroupPart')),
  [BOX_TYPE.HistoricalReportBox]: lazy(() => import('@parts/historicalReport/HistoricalReportPart')),
  [BOX_TYPE.SnapshotBox]: lazy(() => import('@parts/snapshot/snapshotPart')),
  [BOX_TYPE.StateTimelineBox]: lazy(() => import('@parts/stateTimeline/StateTimelinePart')),
  [BOX_TYPE.HsTableBox]: lazy(() => import('@parts/hsTable/HsTablePart')),
  [BOX_TYPE.ProcessSnapshotBox]: lazy(() => import('@parts/processTree/ProcessTreePart')),
} as const;

const MissingBox = ({ type }: { type: BoxType; }) => <div><ExclamationCircleOutlined style={{ fontSize: '1.5em' }} className="text-danger" /> No component for {type}</div>;

export default BoxPage;